<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <v-btn
                class="mx-2"
                fab
                x-small
                color="primary"
                @click="$router.back()"
              >
                <v-icon dark>mdi-arrow-left</v-icon>
              </v-btn>
              <h3 class="font-weight-bold">Scan Product QR Code</h3>
            </v-card-title>
            <v-card-text>
              <!-- Scanner Status -->
              <v-alert
                v-if="!cameraAvailable"
                type="error"
                prominent
                class="mb-4"
              >
                <v-row align="center">
                  <v-col class="grow">
                    Camera not available or permission denied. Please check your device settings.
                  </v-col>
                </v-row>
              </v-alert>

              <!-- QR Scanner Container -->
              <div class="qr-scanner-container">
                <div v-if="isScanning" class="scanner-wrapper">
                  <video ref="video" class="scanner-video" autoplay playsinline></video>
                  <canvas ref="canvas" class="scanner-canvas"></canvas>

                  <!-- Scanner Overlay -->
                  <div class="scanner-overlay">
                    <div class="scanner-box">
                      <div class="corner top-left"></div>
                      <div class="corner top-right"></div>
                      <div class="corner bottom-left"></div>
                      <div class="corner bottom-right"></div>
                    </div>
                  </div>

                  <div class="scanner-instructions">
                    <v-icon color="white" size="40">mdi-qrcode-scan</v-icon>
                    <p class="white--text mt-2">Align QR code within the frame</p>
                  </div>
                </div>

                <!-- Manual Input Option -->
                <div class="mt-4">
                  <v-text-field
                    v-model="manualQRCode"
                    label="Or enter QR code manually"
                    outlined
                    prepend-icon="mdi-keyboard"
                    clearable
                    @keydown.enter="handleManualInput"
                  >
                    <template v-slot:append>
                      <v-btn
                        color="primary"
                        small
                        @click="handleManualInput"
                        :disabled="!manualQRCode"
                      >
                        Submit
                      </v-btn>
                    </template>
                  </v-text-field>
                </div>
              </div>

              <!-- Scanner Controls -->
              <div class="text-center mt-4">
                <v-btn
                  v-if="!isScanning"
                  color="primary"
                  large
                  @click="startScanner"
                  :loading="loading"
                >
                  <v-icon left>mdi-camera</v-icon>
                  Start Scanner
                </v-btn>
                <v-btn
                  v-else
                  color="error"
                  large
                  @click="stopScanner"
                >
                  <v-icon left>mdi-stop</v-icon>
                  Stop Scanner
                </v-btn>
              </div>

              <!-- Scan Result -->
              <v-alert
                v-if="scanResult"
                :type="scanResult.success ? 'success' : 'error'"
                dismissible
                @input="scanResult = null"
                class="mt-4"
              >
                {{ scanResult.message }}
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import jsQR from "jsqr";

export default {
  name: "QRScanner",
  data() {
    return {
      isScanning: false,
      cameraAvailable: true,
      stream: null,
      animationFrame: null,
      manualQRCode: "",
      scanResult: null,
      loading: false,
    };
  },
  beforeDestroy() {
    this.stopScanner();
  },
  methods: {
    async startScanner() {
      this.loading = true;
      this.scanResult = null;

      try {
        // Request camera access
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" }, // Use back camera on mobile
        });

        this.cameraAvailable = true;
        this.isScanning = true;
        this.loading = false;

        // Wait for next tick to ensure video element is rendered
        await this.$nextTick();

        const video = this.$refs.video;
        video.srcObject = this.stream;
        video.setAttribute("playsinline", true); // Required for iOS
        await video.play();

        // Start scanning
        this.scanFrame();
      } catch (error) {
        console.error("Camera access error:", error);
        this.cameraAvailable = false;
        this.isScanning = false;
        this.loading = false;
        this.scanResult = {
          success: false,
          message: "Could not access camera. Please check permissions.",
        };
      }
    },

    stopScanner() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }

      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      this.isScanning = false;
    },

    scanFrame() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;

      if (!video || !canvas) {
        return;
      }

      const context = canvas.getContext("2d");

      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert",
        });

        if (code) {
          // QR code found!
          this.handleQRCodeDetected(code.data);
          return; // Stop scanning after successful detection
        }
      }

      // Continue scanning
      this.animationFrame = requestAnimationFrame(this.scanFrame);
    },

    async handleQRCodeDetected(qrCode) {
      console.log("QR Code detected:", qrCode);
      this.stopScanner();

      // Call API to get product details
      await this.fetchProductDetails(qrCode);
    },

    async handleManualInput() {
      if (!this.manualQRCode) {
        return;
      }

      await this.fetchProductDetails(this.manualQRCode);
    },

    async fetchProductDetails(qrCodeData) {
      this.loading = true;
      this.scanResult = null;

      try {
        const response = await axios.post("scan-product-qr", {
          qr_code_data: qrCodeData,
        });

        if (response.data.success) {
          this.scanResult = {
            success: true,
            message: "Product found! Redirecting...",
          };

          // Redirect to product detail page after a short delay
          setTimeout(() => {
            this.$router.push({
              name: "ScannedProductDetail",
              params: {
                qrCode: qrCodeData,
              },
            });
          }, 1000);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        this.scanResult = {
          success: false,
          message:
            error.response?.data?.message ||
            "Product not found. Please try again.",
        };
      } finally {
        this.loading = false;
        this.manualQRCode = "";
      }
    },
  },
};
</script>

<style scoped>
.qr-scanner-container {
  max-width: 600px;
  margin: 0 auto;
}

.scanner-wrapper {
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
}

.scanner-video {
  width: 100%;
  height: auto;
  display: block;
}

.scanner-canvas {
  display: none;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scanner-box {
  width: 250px;
  height: 250px;
  position: relative;
  border: 2px solid rgba(255, 255, 255, 0.5);
}

.corner {
  position: absolute;
  width: 40px;
  height: 40px;
  border: 3px solid #00ff00;
}

.corner.top-left {
  top: -2px;
  left: -2px;
  border-right: none;
  border-bottom: none;
}

.corner.top-right {
  top: -2px;
  right: -2px;
  border-left: none;
  border-bottom: none;
}

.corner.bottom-left {
  bottom: -2px;
  left: -2px;
  border-right: none;
  border-top: none;
}

.corner.bottom-right {
  bottom: -2px;
  right: -2px;
  border-left: none;
  border-top: none;
}

.scanner-instructions {
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
  text-align: center;
}

.scanner-instructions p {
  margin: 0;
  font-size: 14px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
}
</style>
